import type { DownloadedFile } from '@repo/api-client';

/**
 * Getting the cash book CSV onto somebody's disk.
 *
 * ## Why this is not an anchor pointed at the URL
 *
 * The obvious shape — `<a href={apiUrl + '/v1/reports/cash-book.csv?from=…'}
 * download>` — cannot work here, and it is worth writing down because it looks
 * like it should.
 *
 * **The route authenticates with a bearer token.** `authentication.middleware.ts`
 * reads the principal from `Authorization: Bearer <token>` and from nowhere
 * else: there is no cookie, no session query parameter, no signed link. A plain
 * navigation carries no `Authorization` header, so an anchor pointed at that URL
 * is an unauthenticated request, and the file it saves is a `401` envelope with
 * a `.csv` on the end. Nothing about it looks wrong until somebody opens it.
 *
 * The API now says this itself, on the `200` of both exports: *"Fetch this on
 * the authenticated transport and hand the client the blob."*
 *
 * So the bytes have to be fetched by code that can attach the credential, which
 * is `ApiClient.download` — the same transport, the same credential chosen from
 * the operation's own `security`, the same `ApiError` on a refusal. What is left
 * for this module is the part that is genuinely the browser's: turning bytes
 * already in hand into a saved file.
 *
 * ## The filename is the server's, and this module no longer has an opinion
 *
 * The server sets `Content-Disposition` with a name built from the period, and
 * the document declares it. This module used to *also* rebuild that name from
 * the same period, because `Content-Disposition` is not CORS-safelisted and the
 * API's `Access-Control-Expose-Headers` listed only `x-request-id` — so
 * `response.headers.get('content-disposition')` was `null` for both of this
 * app's targets, the browser back office and an Electron renderer sending
 * `app://blossom` as its `Origin`.
 *
 * **That reconstruction is gone** (their #406 added the header to the list). It
 * was a copy of a server rule living in a different repository, which is the
 * kind of copy that goes stale silently: the day the naming upstream changes,
 * the file is merely named oddly rather than being wrong, and nobody reports it.
 *
 * What remains is {@link CASH_BOOK_DEFAULT_FILENAME}, and it is a different
 * thing on purpose. Exposing a header is not the same as it always arriving — a
 * proxy in front of the API can strip one — and the answer to that must not be
 * a file saved as `undefined`. So there is a default, and it deliberately does
 * not carry the period: it cannot pretend to be the name the server would have
 * chosen, so it cannot drift away from it either.
 */

/**
 * What the cash book export is saved as when the server's own name did not
 * arrive.
 *
 * Names the report and nothing else. **Not** `filenameFor` in the API's
 * `export.service.ts`, which is `` `${report}-${period.from}-to-${period.to}.csv` ``
 * — mirroring that here is exactly what was removed, and a period reappearing in
 * this constant would be that mirror growing back.
 */
export const CASH_BOOK_DEFAULT_FILENAME = 'cash-book.csv';

/**
 * Hand a file the browser has already fetched to whatever saves files.
 *
 * An object URL and a synthetic click, which is the only way to name a download
 * that arrived as bytes: `showSaveFilePicker` is Chromium-only and prompts,
 * which is worse for a file somebody asked for by pressing "Export".
 *
 * The URL is revoked immediately. It pins its blob in memory until it is, and a
 * cash book over a year is not small — on a back-office tab left open all day
 * that is a leak rather than an untidiness. Revoking in the same turn is safe:
 * the click has already started the download, and the browser holds its own
 * reference to the blob for the duration.
 *
 * @param defaultName used only where the response carried no usable name — see
 *   the note at the head of this file.
 */
export function saveDownloadedFile(file: DownloadedFile, defaultName: string): void {
  const url = URL.createObjectURL(file.blob);

  const anchor = document.createElement('a');

  anchor.href = url;
  // A blank or whitespace name would save the file as `""`, which some browsers
  // turn into `download` with no extension. Treated as absent rather than used.
  anchor.download = file.filename?.trim() ? file.filename.trim() : defaultName;

  // Never added to the document. It does not need to be in the tree to be
  // clicked, and a screen that appends one has to remember to remove it on the
  // path where the click throws.
  anchor.click();

  URL.revokeObjectURL(url);
}
